import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Spinner from './Spinner';

const JobPostForm = ({ onSuccess, orgId, orgName }) => {
  const [roleNeeded, setRoleNeeded] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [jobType, setJobType] = useState('Full-time');
  const [requiredSkills, setRequiredSkills] = useState('');
  const [city, setCity] = useState('');
  const [localArea, setLocalArea] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const auth = getAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    const user = auth.currentUser;
    if (!user) {
      setError('You must be logged in to post a job.');
      setLoading(false);
      return;
    }

    if (!orgId) {
      setError('Organization ID is missing. Cannot post job.');
      setLoading(false);
      return;
    }

    try {
      const jobData = {
        orgId: orgId,
        orgName: orgName || 'N/A',
        roleNeeded,
        jobTitle,
        jobType,
        requiredSkills: requiredSkills.split(',').map(skill => skill.trim()).filter(skill => skill),
        location: { city, localArea },
        description,
        postedById: user.uid,
        postedByEmail: user.email,
        postedAt: serverTimestamp(),
        status: 'Open',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('Posting job with data:', jobData);

      const docRef = await addDoc(collection(db, 'cdcJobs'), jobData);
      setSuccessMessage(`Job posted successfully! Job ID: ${docRef.id}`);
      
      // Reset form
      setRoleNeeded('');
      setJobTitle('');
      setJobType('Full-time');
      setRequiredSkills('');
      setCity('');
      setLocalArea('');
      setDescription('');
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error posting job: ", err);
      setError('Failed to post job. ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-3">Post a New Hiring Requirement</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="roleNeeded" className="block text-sm font-medium text-gray-700">Role Needed</label>
          <input 
            type="text" 
            name="roleNeeded" 
            id="roleNeeded" 
            value={roleNeeded} 
            onChange={(e) => setRoleNeeded(e.target.value)} 
            placeholder="e.g., Therapist, Shadow Teacher, Special Educator" 
            required 
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700">Job Title</label>
          <input 
            type="text" 
            name="jobTitle" 
            id="jobTitle" 
            value={jobTitle} 
            onChange={(e) => setJobTitle(e.target.value)} 
            placeholder="e.g., Senior Occupational Therapist" 
            required 
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="jobType" className="block text-sm font-medium text-gray-700">Job Type</label>
          <select 
            name="jobType" 
            id="jobType" 
            value={jobType} 
            onChange={(e) => setJobType(e.target.value)} 
            required 
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
            <option value="Visiting">Visiting/Consultant</option>
            <option value="Contract">Contract</option>
            <option value="Internship">Internship</option>
          </select>
        </div>

        <div>
          <label htmlFor="requiredSkills" className="block text-sm font-medium text-gray-700">Required Skills (comma-separated)</label>
          <input 
            type="text" 
            name="requiredSkills" 
            id="requiredSkills" 
            value={requiredSkills} 
            onChange={(e) => setRequiredSkills(e.target.value)} 
            placeholder="e.g., ABA, Sensory Integration, IEP Planning" 
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">City</label>
            <input 
              type="text" 
              name="city" 
              id="city" 
              value={city} 
              onChange={(e) => setCity(e.target.value)} 
              placeholder="e.g., Mumbai" 
              required 
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="localArea" className="block text-sm font-medium text-gray-700">Local Area (Optional)</label>
            <input 
              type="text" 
              name="localArea" 
              id="localArea" 
              value={localArea} 
              onChange={(e) => setLocalArea(e.target.value)} 
              placeholder="e.g., Andheri West" 
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">Job Description</label>
          <textarea 
            name="description" 
            id="description" 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            rows="4" 
            placeholder="Provide details about the role, responsibilities, qualifications, and any other relevant information."
            required 
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          ></textarea>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}

        <div className="flex justify-end pt-4">
          <button 
            type="submit" 
            disabled={loading} 
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors flex items-center justify-center"
          >
            {loading ? <Spinner size="sm" /> : 'Post Job Requirement'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobPostForm;
